import { useState } from 'react';
import './ui.css';
import { token } from './theme';

/*
 * Table — a sortable data table. Columns declare their own fixed width so the
 * layout stays stable as rows change, and overflowing cell text ellipsizes
 * rather than widening its column. Clicking a sortable header toggles direction;
 * rows start sorted by the first sortable column, ascending.
 */

export interface TableColumn<T> {
  label: string;
  /** Fixed column width, e.g. "25%". */
  width: string;
  render: (row: T) => React.ReactNode;
  /** Providing this makes the column sortable. */
  sortValue?: (row: T) => string | number;
  align?: 'center' | 'right';
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /** Rendered in place of the table when there are no rows. */
  empty?: React.ReactNode;
  /** Width below which the container scrolls horizontally instead of cramping columns. */
  minWidth?: number;
}

const compare = (x: string | number, y: string | number): number =>
  typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));

export function Table<T>({ columns, rows, rowKey, empty, minWidth }: TableProps<T>) {
  const [sort, setSort] = useState({
    index: columns.findIndex((col) => col.sortValue),
    ascending: true,
  });

  if (rows.length === 0) return <>{empty}</>;

  const sortValue = columns[sort.index]?.sortValue;
  const sorted = sortValue
    ? [...rows].sort((a, b) => compare(sortValue(a), sortValue(b)) * (sort.ascending ? 1 : -1))
    : rows;

  const toggleSort = (index: number) =>
    setSort((prev) => ({ index, ascending: prev.index === index ? !prev.ascending : true }));

  return (
    <div style={{ overflow: 'auto', minHeight: 0 }}>
      <table
        style={{
          width: '100%',
          minWidth,
          tableLayout: 'fixed',
          borderCollapse: 'collapse',
          fontFamily: token('font-sans'),
        }}
      >
        <colgroup>
          {columns.map((col, i) => (
            <col key={i} style={{ width: col.width }} />
          ))}
        </colgroup>

        <thead>
          <tr>
            {columns.map((col, i) => {
              const active = i === sort.index;
              return (
                <th
                  key={i}
                  aria-sort={
                    active && col.sortValue
                      ? sort.ascending
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                  style={{ ...headerCell, textAlign: col.align ?? 'left' }}
                >
                  {col.sortValue ? (
                    <button
                      type="button"
                      className="ui-bare-btn"
                      onClick={() => toggleSort(i)}
                      style={{
                        ...sortButton,
                        justifyContent: JUSTIFY[col.align ?? 'left'],
                        color: active ? token('color-gold') : token('color-muted'),
                      }}
                    >
                      {col.label}
                      <span aria-hidden style={{ fontSize: '0.6rem' }}>
                        {active ? (sort.ascending ? '▲' : '▼') : ''}
                      </span>
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {sorted.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((col, i) => (
                <td key={i} style={{ ...bodyCell, textAlign: col.align ?? 'left' }}>
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const JUSTIFY = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
} as const;

const headerCell: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: token('color-surface'),
  borderBottom: `1px solid ${token('color-border')}`,
  padding: '0 var(--space-3) var(--space-2)',
  fontSize: token('font-size-xs'),
  fontWeight: 600,
  letterSpacing: token('letter-spacing-wide'),
  textTransform: 'uppercase',
  color: token('color-muted'),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};

const sortButton: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  width: '100%',
  cursor: 'pointer',
};

const bodyCell: React.CSSProperties = {
  borderBottom: `1px solid ${token('color-border')}`,
  padding: 'var(--space-3)',
  fontSize: token('font-size-sm'),
  color: token('color-text'),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default Table;
